import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export interface Crumb {
  label: string;
  to?: string;
}

interface BreadcrumbsContextValue {
  crumbs: Crumb[];
  setCrumbs: (crumbs: Crumb[]) => void;
}

const BreadcrumbsContext = createContext<BreadcrumbsContextValue | null>(null);

export function BreadcrumbsProvider({ children }: { children: ReactNode }) {
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);
  const value = useMemo(() => ({ crumbs, setCrumbs }), [crumbs]);
  return <BreadcrumbsContext.Provider value={value}>{children}</BreadcrumbsContext.Provider>;
}

export function useBreadcrumbs(): Crumb[] {
  const ctx = useContext(BreadcrumbsContext);
  return ctx?.crumbs ?? [];
}

/**
 * Set breadcrumbs from a route component. Updates whenever the serialized
 * crumb list changes; clears on unmount so stale crumbs from one page never
 * leak into the next.
 */
export function useSetBreadcrumbs(crumbs: Crumb[]) {
  const ctx = useContext(BreadcrumbsContext);
  const key = crumbs.map((c) => `${c.label}|${c.to ?? ''}`).join('>>');
  useEffect(() => {
    ctx?.setCrumbs(crumbs);
    return () => ctx?.setCrumbs([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ctx]);
}
